import {
  Button as ReactAriaButton,
  Input as ReactAriaInput,
  SearchField as ReactAriaSearchField,
} from "react-aria-components";

import { CircularProgress } from "#components/CircularProgress";
import { FormFieldAssistiveText } from "#components/shared/FormFieldAssistiveText";
import { FormFieldHeader } from "#components/shared/FormFieldHeader";
import { ClearIcon, SearchIcon } from "#icons";
import { cn, customTv } from "#utils";

import type {
  InputProps as ReactAriaInputProps,
  SearchFieldProps as ReactAriaSearchFieldProps,
} from "react-aria-components";

interface InputProps extends ReactAriaInputProps {
  className?: string;
}

export interface SearchFieldProps extends ReactAriaSearchFieldProps {
  label?: string;
  assistiveText?: string;
  /**
   * @deprecated Use `inputProps.placeholder` instead.
   */
  placeholder?: string;
  inputProps?: InputProps;
  className?: string;
  /** When true, the search icon becomes a clickable submit button */
  hasSubmitButton?: boolean;
  isPending?: boolean;
}

const inputWrapperStyles = customTv({
  base: `
      text-label-small text-text-primary outline-none border border-outline-input-enabled bg-background-primary rounded-1 leading-md-20 truncate
      hover:border-outline-input-hover hover:ring-1 hover:ring-outline-input-hover
      relative flex items-center gap-3 px-3 py-0-5
      focus-within:!border-outline-input-focus focus-within:ring-1 focus-within:!ring-outline-input-focus
    `,
  variants: {
    isInvalid: {
      true: "border-outline-negative ring-1 ring-outline-negative",
    },
    isDisabled: {
      true: "cursor-not-allowed bg-background-primary text-text-disabled border-outline-input-disabled hover:ring-0 hover:border-outline-input-disabled focus-within:ring-0 focus-within:border-outline-input-disabled",
    },
  },
});

const inputStyles = customTv({
  base: `
    border-0 bg-transparent outline-none
    appearance-none [&::-webkit-search-cancel-button]:hidden
    flex-1
    text-label-small placeholder:text-text-secondary
  `,
  variants: {
    isDisabled: {
      true: "cursor-not-allowed text-text-disabled placeholder:text-text-disabled",
    },
  },
});

export function SearchField({
  label,
  assistiveText,
  placeholder,
  className,
  isDisabled,
  isInvalid,
  inputProps,
  hasSubmitButton,
  isPending,
  "aria-label": ariaLabel,
  ...props
}: SearchFieldProps) {
  const { onSubmit, ...restProps } = props;

  return (
    <ReactAriaSearchField
      aria-label={ariaLabel ?? (label ? undefined : "Search")}
      // Default width to match previous behavior, can be overridden with className to fully control width
      className={cn("group flex w-[12.5rem] flex-col gap-[.15rem]", className)}
      isDisabled={isDisabled}
      isInvalid={isInvalid}
      onSubmit={onSubmit}
      {...restProps}
    >
      {({ isEmpty, isDisabled: isFieldDisabled, isInvalid: isFieldInvalid, state }) => (
        <>
          <FormFieldHeader isDisabled={isFieldDisabled} label={label} />
          <div
            className={inputWrapperStyles({
              isInvalid: isFieldInvalid,
              isDisabled: isFieldDisabled,
            })}
          >
            {isPending ? (
              <CircularProgress size="small" />
            ) : hasSubmitButton ? (
              // Plain HTML button intentionally used here — ReactAriaButton inside
              // SearchField receives clearButtonProps from ButtonContext regardless
              // of slot, which would override onClick and clear the field instead.
              <button
                aria-label="search"
                className={cn(
                  "shrink-0",
                  isFieldDisabled ? "cursor-not-allowed text-text-disabled" : "cursor-pointer",
                )}
                disabled={isFieldDisabled}
                onClick={() => onSubmit?.(state.value)}
                type="button"
              >
                <SearchIcon size={12} />
              </button>
            ) : (
              <SearchIcon className="shrink-0" size={12} />
            )}
            <ReactAriaInput
              size={1}
              {...inputProps}
              className={inputStyles({
                isDisabled: isFieldDisabled,
                className: inputProps?.className,
              })}
              placeholder={inputProps?.placeholder ?? placeholder}
              type="search"
            />
            {!isEmpty ? (
              <ReactAriaButton aria-label="Clear search" slot="clear">
                <ClearIcon />
              </ReactAriaButton>
            ) : null}
          </div>
          <FormFieldAssistiveText isDisabled={isFieldDisabled} isInvalid={isFieldInvalid}>
            {assistiveText}
          </FormFieldAssistiveText>
        </>
      )}
    </ReactAriaSearchField>
  );
}
